/**
 * EscalationConfigSection — destino padrão das transferências do agente
 * (escalationConfig, types 1.2.144 / ponto 6 do blueprint de qualidade).
 *
 * Padrão Intercom Fin: o agente (LLM) decide QUANDO escalar; o dono decide
 * PARA ONDE. Com destino fixo configurado, o runtime expõe apenas a tool
 * do destino com o alvo travado — o modelo não escolhe email/equipe.
 *
 * "Priorizar o responsável pelo lead" NÃO mora mais aqui (2026-10-01): virou
 * opção da distribuição de cada canal (`assignmentConfig.preferLeadOwner`) e
 * vale também para a conversa nova. Esta seção só avisa onde ela está.
 *
 * Semântica do formData — o objeto vai SEMPRE explícito depois que alguém
 * mexe na seção (o `update` do agente só faz `$set`: remover a chave do
 * envio NÃO apagava o destino salvo, e "Modelo decide" nunca era gravado):
 *   - "Modelo decide" ⇒ `{ defaultTarget: 'model_choice' }`.
 *   - "Equipe"/"Atendente" com o alvo escolhido ⇒ config completa.
 *   - Alvo pendente (sem equipe/atendente escolhido) ⇒ `model_choice` —
 *     é o que o aviso da tela promete ("sem escolha, vale Modelo decide").
 */

import React, { useState } from 'react';
import { Select } from '@/shared/components/ui';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import type { CreateAIAgentRequest } from '../services/agentsApi';
import { SectionCard } from './SectionCard';

type TargetChoice = 'model_choice' | 'team' | 'user';
type EscalationConfig = NonNullable<CreateAIAgentRequest['escalationConfig']>;

const TARGET_OPTIONS = [
  { value: 'model_choice', label: 'Modelo decide (equipes e atendentes disponíveis)' },
  { value: 'team', label: 'Sempre uma equipe específica' },
  { value: 'user', label: 'Sempre um atendente específico' },
];

const TARGET_HELP: Record<TargetChoice, string> = {
  model_choice:
    'O agente enxerga as equipes ativas e os atendentes e escolhe o destino conforme o contexto (comportamento padrão).',
  team:
    'Toda transferência vai para a equipe escolhida (rotação automática entre os membros) — mesmo que o cliente peça "falar com uma pessoa".',
  user:
    'Toda transferência vai direto para o atendente escolhido.',
};

interface EscalationConfigSectionProps {
  formData: Partial<CreateAIAgentRequest>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<CreateAIAgentRequest>>>;
}

export function EscalationConfigSection({ formData, setFormData }: EscalationConfigSectionProps) {
  // Alvo em edição (antes de escolher equipe/atendente) vive em estado
  // local — o formData só recebe config COMPLETA e válida.
  const [pendingTarget, setPendingTarget] = useState<TargetChoice | null>(null);

  const saved = formData.escalationConfig;
  const target: TargetChoice = pendingTarget ?? saved?.defaultTarget ?? 'model_choice';

  // Fetch condicionado à seleção (evita N+1 — CLAUDE.md SEMPRE #10)
  const { data: teams = [] } = useTeamsDropdown({ enabled: target === 'team' });
  const { data: users = [] } = useUsersDropdown({ status: 'active', enabled: target === 'user' });

  const teamOptions = teams.map((t: { id: string; name: string }) => ({ value: t.id, label: t.name }));
  const userOptions = users.map((u: { id: string; name: string }) => ({ value: u.id, label: u.name }));

  const setConfig = (config: EscalationConfig) => {
    setFormData((prev) => ({ ...prev, escalationConfig: config }));
  };

  const handleTargetChange = (value: string) => {
    const next = value as TargetChoice;
    // "Modelo decide" grava na hora; "Equipe"/"Atendente" fica pendente até
    // escolher o destino e, enquanto isso, vale "Modelo decide".
    setPendingTarget(next === 'model_choice' ? null : next);
    setConfig({ defaultTarget: 'model_choice' });
  };

  const handleDestinationChange = (value: string) => {
    if (!value) return;
    setPendingTarget(null);
    setConfig(target === 'team' ? { defaultTarget: 'team', teamId: value } : { defaultTarget: 'user', userId: value });
  };

  return (
    <SectionCard
      icon="refresh"
      title="Destino das transferências"
      subtitle="Para onde o agente encaminha a conversa quando precisa escalar para o atendimento humano"
    >
      <div className="space-y-3">
        <Select
          data-testid="escalation-target-select"
          value={target}
          onChange={handleTargetChange}
          options={TARGET_OPTIONS}
        />

        {target === 'team' && (
          <Select
            data-testid="escalation-team-select"
            value={saved?.defaultTarget === 'team' ? saved.teamId ?? '' : ''}
            onChange={handleDestinationChange}
            options={[{ value: '', label: 'Selecione a equipe…' }, ...teamOptions]}
          />
        )}

        {target === 'user' && (
          <Select
            data-testid="escalation-user-select"
            value={saved?.defaultTarget === 'user' ? saved.userId ?? '' : ''}
            onChange={handleDestinationChange}
            options={[{ value: '', label: 'Selecione o atendente…' }, ...userOptions]}
          />
        )}

        <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
          {TARGET_HELP[target]}
          {pendingTarget && (
            <span className="block mt-1 font-medium" style={{ color: 'var(--theme-warning, #b45309)' }}>
              Escolha o destino para aplicar — sem escolha, vale &quot;Modelo decide&quot;.
            </span>
          )}
        </p>

        <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }} data-testid="escalation-lead-owner-hint">
          Quando o cliente tem um lead em aberto, a transferência pode ir primeiro para o responsável por ele. Essa
          opção fica na distribuição de cada canal (&quot;Priorizar o responsável pelo lead&quot;) e já vem ligada.
        </p>
      </div>
    </SectionCard>
  );
}
